// Privacy-safe application session identity for telemetry.
//
// Produces an opaque random session id (no user information), persists it for
// the browser session, and replaces an invalid stored value. The id is sent to
// the backend in `X-App-Session-Id`; the backend validates it and treats it as
// untrusted (it is a correlation key, never a user identity).

export const TELEMETRY_SESSION_STORAGE_KEY = 'hms-telemetry-session'
export const SESSION_HEADER = 'X-App-Session-Id'

// Server-side validation uses the same rules: an opaque id, bounded length,
// safe charset. No PII can be smuggled through.
const SESSION_RE = /^[A-Za-z0-9_-]{1,128}$/

export interface TelemetrySession {
  id: string
}

function isValidSessionId(value: string | null): value is string {
  return !!value && SESSION_RE.test(value)
}

// crypto.randomUUID() when supported, otherwise a best-effort opaque fallback.
// Never embed user info.
function createOpaqueId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const random = Math.random().toString(16).slice(2)
  return `ses-${random}${Date.now().toString(16)}`
}

function readStored(): string | null {
  if (typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem(TELEMETRY_SESSION_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStored(id: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(TELEMETRY_SESSION_STORAGE_KEY, id)
  } catch {
    // Storage unavailable (private mode / quota) — session lives in-memory.
  }
}

/**
 * Returns the current telemetry session id, creating (and persisting) one the
 * first time it is called. An invalid stored value is replaced with a fresh id.
 */
export function getOrCreateTelemetrySession(): TelemetrySession {
  const stored = readStored()
  if (isValidSessionId(stored)) {
    return { id: stored }
  }
  const id = createOpaqueId()
  writeStored(id)
  return { id }
}

/** Explicitly rotate the session id (e.g. on logout / session boundary). */
export function resetTelemetrySession(): TelemetrySession {
  const id = createOpaqueId()
  writeStored(id)
  return { id }
}
